import React, {FormEventHandler, ReactElement, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faScrewdriverWrench, faSpinnerThird, faTruck, faUser} from "@fortawesome/pro-thin-svg-icons";
import {ProcessErrorType, ProcessItemFormType, ProcessType} from "@process/types/process.types";
import {Button, Col, Form, Row} from "react-bootstrap";
import {ClientSelect} from "@src/modules/client/components";
import {DepartmentSelect} from "@src/modules/department/components";
import {initProcess} from "../../utils/axios";
import {GusReportType, Maybe} from "@src/types/global.types";
import {ClientType} from "@client/types/client.types";
import {toast} from "react-toastify";
import {mapProcessToProcessFormType} from "@process/utils/mapper";
import {useNavigate} from "react-router-dom";
import {AddressForm, FormError} from "@src/components";
import {DepartmentType} from "@department/types/department.types";
import {faCloudArrowDown, faPen} from "@fortawesome/pro-regular-svg-icons";
import {fetchFromGus} from "@src/library/axios/tools";
import {AddressType} from "@src/types/address.types";
import {ProcessFormIncomeShipment, ProcessFormShipment} from "@process/components";

interface ProcessFormProps {
    process: ProcessType,
    saveProcess: Function
}

const ProcessFormComponent: React.FC<ProcessFormProps> = (
    {process: processInit, saveProcess}
): ReactElement => {
    const navigate = useNavigate();
    const [process, setProcess] = useState<ProcessType>(processInit);
    const [formErrors, setFormErrors] = useState<ProcessErrorType>({});
    const [isEditBaseSection, setEditBaseSection] = useState<boolean>(false);
    const [isSending, setSending] = useState<boolean>(false)
    const [nipSearch, setNipSearch] = useState<string>('');
    const [nipError, setNipError] = useState<boolean>(false);
    const [nipSearching, setNipSearching] = useState<boolean>(false);

    const removeFromFormErrors = (key: string) => {
        setFormErrors(prevState => {
            let newState = {...prevState};
            delete newState[key];
            return newState;
        })
    }

    const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name;
        const value = e.target.value;

        setProcess(prevState => ({...prevState, [name]: value}));
        removeFromFormErrors(name);
    }

    const onDepartmentChangeHandler = (department?: Maybe<DepartmentType>) => {
        setProcess(prevState => {
            return {...prevState, department: department}
        })
        removeFromFormErrors('departmentAccount');
        initProcess({department: department?.id ?? null}).then((process: ProcessType) => {
            setProcess(prevState => {
                return {...prevState, number: process.number ?? ''}
            });
        });
    }

    const onClientChangeHandler = (client?: Maybe<ClientType>) => {
        removeFromFormErrors('client');

        setProcess((prevState:ProcessType) => {
            return {...prevState, customer: {...prevState.customer ?? {}, client: client}}
        });

        if (client) {
            initProcess({client: client.id}).then((process: ProcessType) => {
                setProcess(prevState => {
                    return {...prevState, customer: process.customer}
                });
            });
        }
    }

    const onCustomerChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name;
        const value = e.target.value;

        setProcess(prevState => ({...prevState, customer: {...prevState.customer, [name]: value}}));
        removeFromFormErrors(name);
    }

    const setCustomerAddress = (address: Maybe<AddressType>) => {
        setProcess((prevState: ProcessType) => ({
            ...prevState, customer: {...prevState.customer, address}
        }));
    }

    const onItemsChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name;
        const value = e.target.value;

        setProcess((prevState: ProcessType): ProcessType => {
            const processItem: ProcessItemFormType = {...prevState.items[0], [name]: value};
            return {...prevState, items: [processItem]};
        });

        removeFromFormErrors(name);
    }

    const getCustomerByNip = () => {
        if (!nipSearch) {
            return;
        }
        setNipSearching(true);
        setNipError(false);
        fetchFromGus(nipSearch)
            .then((gusReport: GusReportType) => {

                if (!gusReport){
                    setNipError(true);
                    return;
                }

                setProcess((prevState:ProcessType) => {
                    return {...prevState, customer: {...prevState.customer ?? {},
                            taxNumber: gusReport.nip,
                            firstName: gusReport.firstName,
                            lastName: gusReport.lastName,
                            companyName: gusReport.companyName,
                            address: gusReport.address
                        }}
                });
            })
            .finally(() => {
                setNipSearching(false)
            })
        ;
    }

    const getClientFromGus = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        getCustomerByNip();
    }

    const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            getCustomerByNip();
            e.preventDefault();
        }
    }

    const onSubmitHandler: FormEventHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        setSending(true);

        saveProcess(mapProcessToProcessFormType(process))
            .then(response => {
                toast.success("Proces został zapisany");
                navigate(`/process/${response.data?.id ?? process.id}`);
            })
            .catch(error => setFormErrors(error.response?.data?.errors || {}))
            .finally(() => setSending(false))
    }

    return (
        <Form onSubmit={onSubmitHandler} className="mb-3">

            <Row>
                <Col>
                    <legend><FontAwesomeIcon icon={faScrewdriverWrench} fixedWidth={true} className="text-purple"/>  Tytuł</legend>
                    <hr className="mb-4"/>
                    <Form.Group className="mb-3">
                        <Form.Control type="text" name="title"
                                      value={process.title ?? ''}
                                      className={formErrors.title ? ' is-invalid' : ''}
                                      onChange={onChangeHandler}
                        />
                        <FormError errors={formErrors?.title} />
                    </Form.Group>
                </Col>
            </Row>

            <Row className="mb-5">
                <Col lg={12}>
                    {isEditBaseSection ? (
                        <div>
                            <Form.Group className="mb-3">
                                <Form.Label className="required">Oddział</Form.Label>
                                <DepartmentSelect
                                    onChange={onDepartmentChangeHandler}
                                    value={process?.department}
                                    className={formErrors.department ? ' is-invalid' : ''}
                                />
                                <FormError errors={formErrors?.department} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="required">Numer</Form.Label>
                                <Form.Control type="text" name="number"
                                              className={formErrors.number ? ' is-invalid' : ''}
                                              value={process.number ?? ''}
                                              onChange={onChangeHandler}
                                />
                                <FormError errors={formErrors?.number} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="required">Data przyjęcia</Form.Label>
                                <Form.Control type="date" name="date"
                                              className={"form-control " + (formErrors.date ? ' is-invalid' : '')}
                                              value={process.date ?? ''}
                                              onChange={onChangeHandler}
                                />
                                <FormError errors={formErrors?.date} />
                            </Form.Group>

                            <div>
                                <Button variant="outline-primary" onClick={() => setEditBaseSection(!isEditBaseSection)}><FontAwesomeIcon icon={faPen} /> Zapisz</Button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <dl className="dl-horizontal">
                                <dt>Numer przyjęcia:</dt>
                                <dd>
                                    <h3 className="fs-2 fw-light m-0">
                                        {process.number}
                                    </h3>
                                    {formErrors?.number && (
                                        <small className="text-danger">
                                            {formErrors.number.map((error, key) => <div key={key}>{error.message}</div>)}
                                        </small>
                                    )}
                                </dd>
                            </dl>
                            <dl className="dl-horizontal">
                                <dt>Oddział:</dt>
                                <dd>{process?.department?.code} - {process?.department?.name}</dd>
                            </dl>
                            <dl className="dl-horizontal border-0">
                                <dt>Data przyjęcia:</dt>
                                <dd>{process.date}</dd>
                            </dl>
                            <dl className="dl-horizontal border-0">
                                <dt></dt>
                                <dd><Button variant="outline-primary" onClick={() => setEditBaseSection(!isEditBaseSection)}><FontAwesomeIcon icon={faPen} /> Edytuj</Button></dd>
                            </dl>
                        </div>
                    )}
                </Col>
            </Row>
            <Row className="mb-5">
                <Col lg={12}>
                    <legend><FontAwesomeIcon icon={faUser} fixedWidth={true} className="text-azure"/> Dane klienta</legend>
                    <hr className="mb-4"/>

                    <Row className="mb-3">
                        <Col sm={6}>
                            <Form.Group className="p-2 bg-lime-lt">
                                <Form.Label className="text-body">Wyszukaj klienta z bazy</Form.Label>

                                <ClientSelect
                                    onChange={onClientChangeHandler}
                                    value={process?.customer?.client ?? null}
                                    isClearable={true}
                                />
                            </Form.Group>
                        </Col>
                        <Col sm={6}>
                            <Form.Group className="p-2 bg-cyan-lt">
                                <Form.Label className="text-body">NIP: Wyszukaj z GUS</Form.Label>

                                <div className="d-flex gap-3">
                                    <div className="w-100">
                                        <Form.Control type="text" name="taxNumber"
                                          value={nipSearch}
                                          onChange={e => setNipSearch(e.target.value)}
                                          isInvalid={nipError}
                                          disabled={nipSearching}
                                          onKeyDown={onKeyDownHandler}
                                        />
                                        {nipError && (
                                            <Form.Control.Feedback type="invalid">
                                                Brak wyników wyszukiwania dla podanego numeru NIP
                                            </Form.Control.Feedback>
                                        )}
                                    </div>
                                    <div>
                                        <Button variant={"outline-primary"} onClick={getClientFromGus}>
                                            <FontAwesomeIcon icon={nipSearching ? faSpinnerThird : faCloudArrowDown} fixedWidth />
                                            &nbsp;
                                            Pobierz z GUS
                                        </Button>
                                    </div>
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={6}>
                            <Row>
                                <Col sm={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Imię</Form.Label>
                                        <Form.Control type="text" name="firstName"
                                                      className={formErrors?.customer?.firstName ? ' is-invalid' : ''}
                                                      value={process?.customer?.firstName ?? ''}
                                                      onChange={onCustomerChangeHandler}
                                        />
                                        <FormError errors={formErrors?.customer?.firstName} />
                                    </Form.Group>
                                </Col>
                                <Col sm={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nazwisko</Form.Label>
                                        <Form.Control type="text" name="lastName"
                                                      className={formErrors?.customer?.lastName ? ' is-invalid' : ''}
                                                      value={process?.customer?.lastName ?? ''}
                                                      onChange={onCustomerChangeHandler}
                                        />
                                        <FormError errors={formErrors?.customer?.lastName} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Firma</Form.Label>
                                        <Form.Control type="text" name="companyName"
                                                      className={formErrors?.customer?.companyName ? ' is-invalid' : ''}
                                                      value={process?.customer?.companyName ?? ''}
                                                      onChange={onCustomerChangeHandler}
                                        />
                                        <FormError errors={formErrors?.customer?.companyName} />
                                    </Form.Group>
                                </Col>
                                <Col sm={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>NIP</Form.Label>
                                        <Form.Control type="text" name="taxNumber"
                                                      className={formErrors?.customer?.taxNumber ? ' is-invalid' : ''}
                                                      value={process?.customer?.taxNumber ?? ''}
                                                      onChange={onCustomerChangeHandler}
                                        />
                                        <FormError errors={formErrors?.customer?.taxNumber} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>E-mail</Form.Label>
                                        <Form.Control type="text" name="email"
                                                      className={formErrors.customer?.email ? ' is-invalid' : ''}
                                                      value={process?.customer?.email ?? ''}
                                                      onChange={onCustomerChangeHandler}
                                        />
                                        <FormError errors={formErrors?.customer?.email} />
                                    </Form.Group>
                                </Col>
                                <Col sm={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="required">Telefon</Form.Label>
                                        <Form.Control type="text" name="phone"
                                                      className={formErrors?.customer?.phone ? ' is-invalid' : ''}
                                                      value={process?.customer?.phone ?? ''}
                                                      onChange={onCustomerChangeHandler}
                                        />
                                        <FormError errors={formErrors?.customer?.phone} />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Col>
                        <Col sm={6}>
                            <AddressForm
                                address={process?.customer?.address}
                                setAddress={setCustomerAddress}
                                formErrors={formErrors?.customer?.address}
                            />
                        </Col>
                    </Row>

                </Col>
            </Row>

            <Row className="mb-5">
                <Col>
                    <legend><FontAwesomeIcon icon={faScrewdriverWrench} fixedWidth={true} className="text-azure"/> Szczegóły urządzenia i usterka</legend>
                    <hr className="mb-4"/>
                    {process.items?.map((processItem: ProcessItemFormType, keyItem: string | number) => {
                        const itemErrors = formErrors?.items && formErrors?.items[0];
                        return (
                            <div key={keyItem}>
                                <Row>
                                    <Col sm={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Model</Form.Label>
                                            <Form.Control type="text" name="name"
                                                          className={itemErrors?.name ? ' is-invalid' : ''}
                                                          value={processItem.name ?? ''}
                                                          onChange={onItemsChangeHandler}
                                            />
                                            <FormError errors={itemErrors?.name} />
                                        </Form.Group>
                                    </Col>
                                    <Col sm={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Numer seryjny</Form.Label>
                                            <Form.Control type="text" name="serialNumber"
                                                          className={itemErrors?.serialNumber ? ' is-invalid' : ''}
                                                          value={processItem.serialNumber ?? ''}
                                                          onChange={onItemsChangeHandler}
                                            />
                                            <FormError errors={itemErrors?.serialNumber} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Opis usterki</Form.Label>
                                            <Form.Control as="textarea" name="note" rows={3}
                                                          onChange={onItemsChangeHandler}
                                                          value={processItem.note ?? ''}
                                            />
                                            <FormError errors={itemErrors?.note} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </div>
                        )
                    })}
                </Col>
            </Row>

            <Row className="mb-5">
                <Col>
                    <legend><FontAwesomeIcon icon={faTruck} fixedWidth={true} className="text-azure"/> Odbiór i zwrot urządzenia</legend>
                    <hr className="mb-4"/>

                    <Row>
                        <Col sm={6}>
                            <ProcessFormIncomeShipment
                                process={process}
                                formErrors={formErrors}
                                setProcess={setProcess}
                                removeFromFormErrors={removeFromFormErrors}
                            />
                        </Col>
                        <Col sm={6}>
                            <ProcessFormShipment
                                process={process}
                                formErrors={formErrors}
                                setProcess={setProcess}
                                removeFromFormErrors={removeFromFormErrors}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>


            <Row className="mb-3 mt-5">
                <Col>
                    <Form.Group className="mb-3">
                        <Form.Label>Uwagi do zgłoszenia</Form.Label>
                        <Form.Control as="textarea" name="note" rows={3}
                                      onChange={onChangeHandler}
                                      value={process.note ?? ''}
                        />
                        <FormError errors={formErrors?.note} />
                    </Form.Group>
                </Col>
            </Row>

            {/*<hr className="mb-4"/>*/}

            <Button size="lg" type="submit" variant="primary" disabled={isSending}>
                {isSending ? (
                    <><FontAwesomeIcon icon={faSpinnerThird} spin={true}/> wysyłam</>
                ) : 'Zapisz'}
            </Button>
        </Form>
    );
}

export default ProcessFormComponent;